import { Box, Grid, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCellCheckboxRenderer,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
  gridClasses,
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFilterModel } from '../../hooks/useFilterModel';
import Scrollbar from '../scrollbar';
import { SkeletonDataGrid } from '../skeleton/SkeletonDataGrid';
import useWindowSize from '../window-size/windowSize';
import { CustomFilter, CustomQuickFilter, createColumns } from './custom-filters';
import { CustomSettings } from './grid-settings';
import {
  filterRows,
  getGridFilterFieldValues,
  GetGridKeys,
  loadSavedFilters,
  loadSavedSorts,
  onSortModelChange,
  reconcileColumnOrder,
  setInitFilters,
} from './dataGridHelper';
import { removeUserSettings } from '../../redux/slices/user';
import DataGridPersistSettings from './DataGridPersistSettings';
import DeactivatedTooltipRow from './DeactivatedTooltipRow';

CustomDataGrid.propTypes = {
  data: PropTypes.array,
  gridColumns: PropTypes.array,
  id: PropTypes.string,
  gridId: PropTypes.string,
  presetFilter: PropTypes.string,
  enableRowClick: PropTypes.bool,
  CustomLeftToolbar: PropTypes.any,
  maxValue: PropTypes.object,
  boxSX: PropTypes.object,
  sx: PropTypes.object,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  isModal: PropTypes.bool,
  applyQuickFilter: PropTypes.bool,
  disabled: PropTypes.bool,
  dontGetRowHeight: PropTypes.bool,
  forceReRender: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]), // array works bec technically data in the array change thats forcing a re-render useful for checkboxes
  hideToolbar: PropTypes.bool,
  hideFilterSettings: PropTypes.bool,
  scrollbarHeight: PropTypes.number,
  setActiveRows: PropTypes.func,
  sort: PropTypes.array,
  pinnedColumns: PropTypes.object,
  displayCheckboxRight: PropTypes.bool,
  isLoading: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  apiRef: PropTypes.object,
  advancedFilter: PropTypes.bool,
  advancedSearchBase: PropTypes.string,
  tableBoxSX: PropTypes.object,
  checkboxTooltipCheck: PropTypes.func,
  checkboxTooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  noRowsMessage: PropTypes.string,
  defaultHiddenColumns: PropTypes.array,
  permanentHiddenColumns: PropTypes.array,
  disableCellHighlight: PropTypes.bool,
  customFilter: PropTypes.func,
  persistentSort: PropTypes.array,
};
export default function CustomDataGrid({
  data,
  gridColumns,
  id = 'id',
  gridId = 'id',
  enableRowClick = false,
  applyQuickFilter = false,
  CustomLeftToolbar = () => <></>,
  maxValue,
  boxSX = { width: '100%', height: 'calc(100vh - 240px)' },
  tableBoxSX = { height: 'calc(100% - 25px)' },
  sx = {},
  onRowClick = () => {},
  onCellClick = () => {},
  isModal = false,
  setActiveRows,
  sort = [],
  pinnedColumns = {},
  displayCheckboxRight = false,
  disabled = false,
  isLoading = null,
  dontGetRowHeight = false,
  scrollbarHeight = 100,
  hideToolbar = false,
  hideFilterSettings = false,
  apiRef,
  presetFilter,
  forceReRender,
  advancedFilter = false,
  disableCellHighlight = false,
  checkboxTooltipCheck = null,
  checkboxTooltipText = null,
  advancedSearchBase,
  noRowsComponent,
  defaultHiddenColumns = [],
  permanentHiddenColumns = [],
  quickfilterHiddenSearchCol = null,
  customFilter = null,
  persistentSort = [],
  ...dataGridProps
}) {
  const size = useWindowSize();
  const gridRef = useGridApiRef();
  const { filterStorageKey, gridKey, sortStorageKey, quickFilterKey, baseSearchTextStorageKey } = GetGridKeys(gridId);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [quickFilter, setQuickFilter] = useState('');
  const [filters, setFilters] = useState(!isModal ? loadSavedFilters(filterStorageKey) : {});
  const savedSorts = !isModal ? loadSavedSorts(sortStorageKey) : null;
  const [sortModel, setSortModel] = useState([
    ...persistentSort,
    ...(savedSorts?.length > 0 ? savedSorts : sort || []),
  ]);
  const [columnsState, setColumnsState] = useState();
  const [resetKey, setResetKey] = useState([0, 0]);
  const [lastSize, setLastSize] = useState([0, 0]);
  const dispatch = useDispatch();

  const defaultColumnVisibilityModel = [...(defaultHiddenColumns || []), ...(permanentHiddenColumns || [])].reduce(
    (acc, col) => {
      acc[col] = false;
      return acc;
    },
    {}
  );

  const CustomNoRowsOverlay = () => (
    <Box sx={{ p: 32, textAlign: 'center', width: '100%' }}>
      {noRowsComponent || (
        <Typography variant="subtitle2" color="text.main">
          No rows
        </Typography>
      )}
    </Box>
  );

  useEffect(() => {
    if (!anchorEl) setResetKey(size);
    setLastSize(size);
  }, [anchorEl, size]);

  useEffect(() => {
    const currentState = apiRef?.current?.exportState() || gridRef?.current?.exportState();
    setColumnsState(currentState);
  }, [apiRef, gridRef]);

  const columnReorder = (colState) => {
    try {
      const updatedColumns = colState?.columns
        ? gridColumns
            .map((col) => ({ ...col, ...colState?.columns?.dimensions[col.field] }))
            .sort((a, b) => {
              const indexA = colState?.columns?.orderedFields?.indexOf(a.field);
              const indexB = colState?.columns?.orderedFields?.indexOf(b.field);
              if (indexA > -1 && indexB > -1) return indexA - indexB;
              return 0;
            })
        : gridColumns;
      return updatedColumns;
    } catch (err) {
      console.log(err);
      return gridColumns;
    }
  };

  const [filteredRows, setFilteredRows] = useState(data); //to be used for getting subcategory select options to match against the filtered categories

  const [pinnedCols, setPinnedCols] = useState(pinnedColumns);

  useEffect(() => setInitFilters(setFilters, filterStorageKey, quickFilterKey), [quickFilterKey, filterStorageKey]);

  const columns = useMemo(
    () => createColumns(columnReorder(columnsState), filters, setAnchorEl, setActiveFilter, gridId, activeFilter),
    [activeFilter, filters, gridId, columnsState, forceReRender]
  );

  const { gridPersists, gridSettings, updateGridSettings } = DataGridPersistSettings({ gridId, columns });

  // on unmount, flush the debounced function to ensure any pending updates are applied
  useEffect(() => () => updateGridSettings.flush(), [updateGridSettings]);

  const initialState = useMemo(() => {
    if (gridSettings?.gridState) return gridSettings.gridState;
    return { columns: { columnVisibilityModel: defaultColumnVisibilityModel } };
  }, [gridSettings, defaultColumnVisibilityModel]);

  const filterModel = useFilterModel(filters);

  const rowsAfterQuickFilter = useMemo(
    () =>
      filterRows(
        data,
        quickFilter,
        quickfilterHiddenSearchCol ? [...columns, quickfilterHiddenSearchCol] : columns,
        customFilter
      ),
    [data, quickFilter]
  );

  const fieldValues = useMemo(
    () => getGridFilterFieldValues(columns, rowsAfterQuickFilter, filters),
    [rowsAfterQuickFilter, filteredRows, gridColumns]
  );

  useEffect(() => {
    const visibleRowIds = gridFilteredSortedRowIdsSelector(
      apiRef?.current?.state || gridRef?.current?.state,
      apiRef?.current?.instanceId || gridRef?.current?.instanceId
    );

    const visibleRows = visibleRowIds?.map((rowId) => {
      const rowParams = apiRef?.current?.getRow(rowId) || gridRef?.current?.getRow(rowId);

      return rowParams;
    });
    setFilteredRows(visibleRows);
    if (setActiveRows) {
      setActiveRows(visibleRows);
    }
  }, [quickFilter, filterModel, data, gridRef, apiRef]);

  const handleGridWidthChange = () => {
    const currentState = apiRef?.current?.exportState() || gridRef?.current?.exportState();
    if (gridPersists) updateGridSettings(currentState);
    setColumnsState(currentState);
  };
  const handleGridOrderChange = () => {
    const currentState = apiRef?.current?.exportState() || gridRef?.current?.exportState();
    if (gridPersists) updateGridSettings(currentState);
    setColumnsState(currentState);
  };

  const columnVisibilityModel =
    columnsState?.columns?.columnVisibilityModel ??
    initialState.columns?.columnVisibilityModel ??
    defaultColumnVisibilityModel;

  const handleGridVisibilityChange = (model) => {
    const newState = { ...columnsState, columns: { ...columnsState?.columns, columnVisibilityModel: model } };
    setColumnsState(newState);
    if (gridPersists) updateGridSettings(newState);
  };

  const resetGridState = () => {
    // remove settings from database and localstorage
    localStorage.removeItem(gridKey);
    localStorage.removeItem(filterStorageKey);
    localStorage.removeItem(sortStorageKey);
    columnsState?.columns?.orderedFields?.forEach((c) => {
      localStorage.removeItem(`${baseSearchTextStorageKey}-${c}`);
    });
    setFilters({});
    setSortModel([...persistentSort, ...(sort || [])]);
    setColumnsState({
      columns: {
        columnVisibilityModel: defaultColumnVisibilityModel,
        dimensions: {},
        orderedFields: gridColumns.map((c) => c.field),
      },
    });
    if (gridSettings) dispatch(removeUserSettings([gridKey]));
  };

  const checkboxRenderer = useMemo(
    () => ({
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      sortable: true,
      sortComparator: (a, b) => Number(Boolean(b)) - Number(Boolean(a)),
      ...(checkboxTooltipCheck
        ? {
            renderCell: (params) =>
              checkboxTooltipCheck(params.row) ? (
                <Tooltip placement={displayCheckboxRight ? 'top' : 'left'} arrow title={checkboxTooltipText}>
                  <Box component="span" sx={{ pointerEvents: 'auto' }}>
                    <GridCellCheckboxRenderer {...params} />
                  </Box>
                </Tooltip>
              ) : (
                <GridCellCheckboxRenderer {...params} />
              ),
          }
        : {}),
    }),
    [checkboxTooltipCheck, displayCheckboxRight, checkboxTooltipText]
  );

  if (!initialState) return null;

  return (
    <Box sx={{ width: anchorEl && lastSize[0] !== resetKey[0] ? 'calc(100% - 10px)' : '100%' }} key={resetKey}>
      <Scrollbar
        onClickCapture={(e) => {
          if (disabled) e.stopPropagation();
        }}
        sx={{
          height: scrollbarHeight ? `max(${scrollbarHeight}vh - 170px, 180px)` : 'auto',
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <Grid item xs={12}>
          <Box sx={boxSX}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
              {hideToolbar ? (
                <Grid item />
              ) : (
                <Grid item xs={8}>
                  <CustomLeftToolbar />
                </Grid>
              )}

              {!hideFilterSettings && (
                <Grid item xs={4}>
                  <Grid container direction="row" justifyContent="flex-end" alignItems="flex-end">
                    <Box sx={{ width: 'calc(100% - 40px)' }}>
                      <CustomQuickFilter
                        gridId={gridId}
                        setActiveFilter={setActiveFilter}
                        setQuickFilter={setQuickFilter}
                        presetFilter={presetFilter}
                        setFilters={setFilters}
                        isModal={isModal}
                        applyQuickFilter={applyQuickFilter}
                        advancedFilter={advancedFilter}
                        advancedSearchBase={advancedSearchBase}
                        columns={columnsState?.columns?.orderedFields}
                      />
                    </Box>
                    <CustomSettings
                      columnsState={columnsState}
                      setColumnsState={setColumnsState}
                      gridColumns={gridColumns}
                      resetGridState={resetGridState}
                      permanentHiddenColumns={permanentHiddenColumns}
                      handleGridSettingsUpdate={updateGridSettings}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Box sx={tableBoxSX}>
              <DataGridPro
                initialState={{
                  ...initialState,
                  preferencePanel: { open: false },
                  columns: {
                    ...initialState.columns,
                    orderedFields: Array.isArray(initialState.columns?.orderedFields)
                      ? reconcileColumnOrder(
                          initialState.columns.orderedFields,
                          columns.map((c) => c.field)
                        )
                      : [],
                    columnVisibilityModel: {
                      // Hide columns from hideColumns array
                      ...[...(defaultHiddenColumns || []), ...(permanentHiddenColumns || [])].reduce((acc, col) => {
                        acc[col] = false;
                        return acc;
                      }, {}),
                      // Spread any existing visibility model
                      ...(initialState.columns?.columnVisibilityModel || {}),
                    },
                  },
                  sorting: { sortModel },
                }}
                getRowHeight={dontGetRowHeight ? null : () => 'auto'}
                rows={rowsAfterQuickFilter}
                getRowId={(row) => row[id]}
                columns={
                  displayCheckboxRight
                    ? [...columns, { ...checkboxRenderer, width: 50 }]
                    : checkboxTooltipCheck
                    ? [checkboxRenderer, ...columns]
                    : columns
                }
                //dont change components to slots it breaks the skeleton loading overlay
                components={{
                  LoadingOverlay: SkeletonDataGrid,
                  NoRowsOverlay: CustomNoRowsOverlay,
                  Row: DeactivatedTooltipRow,
                }}
                componentsProps={{
                  loadingOverlay: { height: `calc(${boxSX.height} - 100px)` },
                }}
                filterModel={filterModel}
                disableRowSelectionOnClick={!enableRowClick}
                loading={isLoading !== null ? isLoading : !data.length}
                onRowClick={onRowClick}
                onCellClick={onCellClick}
                pinnedColumns={pinnedCols}
                onPinnedColumnsChange={(params) => {
                  setPinnedCols(params.pinnedColumns);
                }}
                onColumnWidthChange={handleGridWidthChange}
                onColumnOrderChange={handleGridOrderChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleGridVisibilityChange}
                sx={{
                  ...sx,
                  minHeight: 130,
                  mt: 1,
                  '& .MuiDataGrid-row:hover': {
                    cursor: enableRowClick ? 'pointer' : 'default',
                  },
                  '& .disabled': {
                    pointerEvents: 'none', // Prevents hover and click
                    color: 'rgba(0, 0, 0,0.65)', // Text color for disabled state
                    backgroundColor: 'rgba(0, 0, 0, 0.12)', // Background for disabled state
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)', // Maintain background on hover
                      color: 'rgba(0, 0, 0,0.65)', // Text color for disabled state
                    },
                  },
                  // allow specific elements inside a disabled row to remain clickable:
                  '& .disabled .allow-click': {
                    pointerEvents: 'auto' /* override to make button clickable */,
                    opacity: 1,
                  },
                  '& .disabled.allow-click': {
                    pointerEvents: 'auto' /* override to make button clickable */,
                    opacity: 1,
                  },
                  '& .MuiBadge-badge': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-pinnedColumns': {
                    boxShadow: '0px 0 3px 0 rgba(0, 0, 0, 0.15)',
                    zIndex: 2,
                    },
                   '& .no-x-padding' : {
                    paddingLeft: 0,
                    paddingRight: 0
                  },
                  '& .no-resizable .MuiDataGrid-iconSeparator': {
                    display: 'none'
                  },
                  ...(disableCellHighlight
                    ? {
                        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                          outline: 'none',
                        },
                      }
                    : {}),
                }}
                disableColumnMenu
                disableColumnFilter
                // disableColumnSelector
                apiRef={apiRef || gridRef}
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  const newSort = [...persistentSort, ...model];
                  onSortModelChange(setSortModel, newSort, sortStorageKey);
                }}
                {...dataGridProps}
              />
            </Box>
          </Box>

          <CustomFilter
            gridId={gridId}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setActiveFilter={setActiveFilter}
            activeFilter={activeFilter}
            filters={filters}
            setFilters={setFilters}
            fieldValues={fieldValues}
            columns={columns}
            maxValue={maxValue}
          />
        </Grid>
      </Scrollbar>
    </Box>
  );
}
